import { CurrencyPipe } from '@angular/common';
import { Component, OnInit, computed, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import frenchi18n from '@assets/i18n/fr.json';
import { Account } from '@core/model/domain/account';
import { environment } from '@environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { CancelOpenBankingPopinComponent } from '@shared/components/cancel-open-banking-popin/cancel-open-banking-popin.component';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { assert } from 'ts-essentials';
import { PopinComponent } from '../../../../shared/components/popin/popin.component';
import { SelectAccountsVM } from './select-accounts.viewmodel';

type ValidForm = null;

@Component({
  selector: 'app-select-accounts',
  templateUrl: './select-accounts.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    CurrencyPipe,
    CancelOpenBankingPopinComponent,
    PopinComponent,
  ],
})
export class SelectAccountsComponent implements OnInit {
  private readonly storeService = inject(StoreService);
  isCancelPopinOpen = false;
  isMakeAChoicePopinOpen = false;
  accountsSelectionForm: FormGroup;
  selectingAccounts = false;
  countSelectedBank: number = 0;
  selectAccountsVM: SelectAccountsVM;
  readonly selectedBank = this.storeService.selectOrThrow('SELECTED_BANK');
  readonly foundAccounts = this.storeService.selectOrThrow('FOUND_ACCOUNTS');
  readonly isMultipleAccount = computed(() => this.foundAccounts().length > 1);
  readonly isTestBankConnector = computed(
    () => this.selectedBank().uid === environment.testBankConnector && environment.env !== 'prod'
  );

  constructor(
    private readonly processEventService: ProcessEventService,
    private readonly tracker: TrackerService
  ) {
    this.selectAccountsVM = new SelectAccountsVM(this.processEventService);

    this.accountsSelectionForm = new FormGroup(this.buildAccountsFormControls());
    this.accountsSelectionForm.setValidators(() => this.accountsSelectionValidator());
  }

  ngOnInit(): void {
    const isMultipleAccount = this.isMultipleAccount();
    this.tracker.trackPage({
      taggingPage: 'SELECT_ACCOUNTS',
      taggingPayload: { isMultipleAccount },
    });
  }

  async submit() {
    if (!this.accountsSelectionForm.valid) {
      this.tracker.trackClick({
        taggingPage: 'SELECT_ACCOUNTS',
        click: frenchi18n.continue,
        taggingPayload: { isMultipleAccount: this.isMultipleAccount() },
      });
      this.isMakeAChoicePopinOpen = true;
      return;
    }

    this.tracker.trackClick({
      taggingPage: 'SELECT_ACCOUNTS',
      click: frenchi18n.continue,
      taggingPayload: { isMultipleAccount: this.isMultipleAccount() },
    });
    const selectedAccounts = this.getSelectedAccountsFromForm();
    await this.selectAccountsVM.submit(selectedAccounts);
  }

  closeCancelPopin() {
    this.isCancelPopinOpen = false;
  }

  closeMakeAChoicePopinPopin() {
    this.isMakeAChoicePopinOpen = false;
  }

  private buildAccountsFormControls(): Record<string, FormControl<boolean | string>> {
    const foundAccounts = this.foundAccounts();
    const idControls = foundAccounts.reduce<Record<string, FormControl<boolean>>>((accumulator, account) => {
      accumulator[account.id] = new FormControl<boolean>(false, { nonNullable: true });
      return accumulator;
    }, {});

    if (this.isTestBankConnector()) {
      const ibanControls = foundAccounts.reduce<Record<string, FormControl<boolean | string>>>(
        (acc, account) => {
          acc[account.iban ?? `iban-${account.id}`] = new FormControl<string>(account.iban ?? '', {
            nonNullable: true,
          });
          return acc;
        },
        {}
      );

      return { ...ibanControls, ...idControls };
    }

    return idControls;
  }

  private accountsSelectionValidator(): ValidForm | { noAccountSelected: true } {
    const isAtLeastOneAccountSelected = this.getNumbersOfAccountsSelected() > 0;

    if (!isAtLeastOneAccountSelected) {
      return { noAccountSelected: true };
    }

    return null;
  }

  private getNumbersOfAccountsSelected(): number {
    return this.foundAccounts().filter((account) => {
      return this.accountsSelectionForm.get(account.id)?.value === true;
    }).length;
  }

  private getSelectedAccountsFromForm(): Account[] {
    const selectedAccounts: Account[] = this.foundAccounts().filter(
      (account) => this.accountsSelectionForm.get(account.id)?.value === true
    ) as Account[];

    assert(selectedAccounts != null, 'We must have selected accounts.');

    this.countSelectedBank = this.getNumbersOfAccountsSelected();
    this.selectingAccounts = true;

    return selectedAccounts;
  }

  retryBankConnectionWithPartner() {
    this.tracker.trackClick({
      click: frenchi18n.come_back_to_classic_form,
      taggingPage: 'SELECT_ACCOUNTS',
      taggingPayload: { isMultipleAccount: this.isMultipleAccount() },
    });

    this.isCancelPopinOpen = true;
  }
}
